'use client';

import { useEffect, useState } from 'react';
import { INSERT_GRACE_MS, isSafeToInsertAt } from '@/lib/stableInsert';

/**
 * `false` until changing the layout at `getTop()` cannot move anything the reader
 * can see, then `true` forever.
 *
 * See `lib/stableInsert` for the measurements. In short: a layout change inside the
 * reader's viewport is worth up to 0.87 CLS, and the same change above or below the
 * viewport is worth 0.000 — so the answer is to wait for a scroll rather than to
 * shift more cleverly.
 *
 * `getTop` returns viewport coordinates (`getBoundingClientRect().top`) or `null`
 * when there is nothing to measure yet, in which case we keep waiting.
 *
 * The grace timer is the honesty valve: a reader who parks with the change point
 * mid-screen and never scrolls would otherwise never get their own content. After
 * `INSERT_GRACE_MS` it happens anyway — one shift is better than withholding the
 * buyer's saved-search matches indefinitely.
 */
export function useLayoutSafe(getTop: () => number | null, graceMs = INSERT_GRACE_MS): boolean {
  const [safe, setSafe] = useState(false);

  useEffect(() => {
    let done = false;
    const check = () => {
      const top = getTop();
      if (top === null) return false;
      return isSafeToInsertAt({ top, viewportHeight: window.innerHeight });
    };
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(timer);
      setSafe(true);
    };
    function onScroll() {
      if (check()) finish();
    }

    const timer = setTimeout(finish, graceMs);
    if (check()) {
      finish();
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
    return () => {
      done = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(timer);
    };
    // `getTop` is a stable closure over a ref/selector by contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graceMs]);

  return safe;
}
